import React, { useEffect } from 'react';
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  makeStyles,
  Radio,
  RadioGroup,
  Slide,
} from '@material-ui/core';
import { Close, ArrowForwardRounded } from '@material-ui/icons';
import swal from 'sweetalert';
import logger from '../../../../utils/logger.js';
import Button from '../../../../components/CustomButtons/Button';
import CreditMetrics from '../CreditLimit/CreditMetrics';
import currencyFormat from '../../../../utils/toIndianCurrencyString';
import { mappedCurrentActiveRole } from '../../../../utils/roleMapping';
import apiCaller from '../../../../utils/apiCallerOutstanding';
import firebase from '../../../../config/config';

const Transition = React.forwardRef((props, ref) => (
  <Slide direction="down" {...props} ref={ref} />
));

const useStyles = makeStyles(theme => ({
  title: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  cards: {
    backgroundColor: '#E5EBFB',
    marginBottom: '15px',
    padding: '12px 16px',
  },
  flexCol: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  transporter: {
    color: '#163BAC',
    fontSize: '16px',
    fontWeight: 500,
  },
  colFlexStart: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  labelText: {
    color: '#8F8F8F',
    fontSize: '13px',
    fontWeight: 500,
  },
  weight: {
    fontWeight: 500,
  },
  submitButton: {
    backgroundColor: '#4065F6',
    textTransform: 'none',
    padding: '10px',
    fontSize: '14px',
    minWidth: '100px',
  },
  cancelButton: {
    color: '#2F2F2F',
    textTransform: 'none',
    padding: '10px',
    fontSize: '14px',
    minWidth: '100px',
  },
  arrow: {
    marginLeft: '10px',
    marginRight: '10px',
  },
  creditLimitSection: {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  creditLimit: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    flexWrap: 'wrap',
  },
}));

const CreditLimitEditDialog = ({
  creditLimitEditDialog,
  setCreditLimitEditDialog,
  transporterId,
  TransporterPercentage,
  activeRole,
  branch,
  branchId,
  branchLimit,
  getBranchesCreditLimit,
  setActivityRefresh,
  threshold,
  isThresholdExceeded,
  isCreditLimitExceeded,
}) => {
  const classes = useStyles();
  const [extendLimit, setExtendLimit] = React.useState(false);
  const [extendThreshold, setExtendThreshold] = React.useState(false);

  const [creditRemark, setCreditRemark] = React.useState('');
  const [newCreditLimit, setNewCreditLimit] = React.useState('');
  const [newCreditThreshold, setNewCreditThreshold] = React.useState('');

  const [creditData, setCreditData] = React.useState({});
  const [creditDataLoading, setCreditDataLoading] = React.useState(false);
  const [creditLimitUpdating, setCreditLimitUpdating] = React.useState(false);
  const currentUserDetails = firebase.auth().currentUser;
  const { Aa: token } = currentUserDetails;
  const currentActiveRole = mappedCurrentActiveRole(activeRole);
  const getCreditLimitDetails = async () => {
    setCreditDataLoading(true);
    const branchID = branchId ? `&branch_id=${branchId}` : '';
    const endPoint = `pipe/transporter/v2/branch-wise-credit-limit?role=${currentActiveRole}&transporter_id=${transporterId}${branchID}`;
    await apiCaller(endPoint, {}, `Bearer ${token}`, 'nh-base', 'get')
      .then(response => {
        const result = response?.data;
        setCreditData(result);
      })
      .catch(e => {})
      .finally(() => {
        setCreditDataLoading(false);
      });
  };

  const currentThresholdCount =
    creditData?.transporter_info?.current_threshold_trip_count
      ?.current_threshold_trip_count;

  const handleClose = () => {
    setCreditLimitEditDialog(false);
    setExtendLimit(false);
    setExtendThreshold(false);
    setCreditRemark('');
    setNewCreditLimit('');
    setNewCreditThreshold('');
  };

  async function updateCreditCount() {
    setCreditLimitUpdating(true);
    const body = {
      role: currentActiveRole,
      transporter_id: transporterId,
      current_threshold_trip_count: Number(currentThresholdCount),
      requested_threshold_trip_count: newCreditThreshold,
      remarks: creditRemark,
    };

    const endPoint = 'pipe/transporter/threshold-trip-count';
    try {
      const response = await apiCaller(
        endPoint,
        body,
        `Bearer ${token}`,
        'nh-base',
        'post'
      );
      swal('Success!', `${response?.data?.message}`, 'success');
      handleClose();
    } catch (error) {
      swal('Error!', `${error?.response?.data?.Error}`, 'error');
    }
    setCreditLimitUpdating(false);
  }

  async function updateCreditLimit() {
    setCreditLimitUpdating(true);
    const body = {
      role: currentActiveRole,
      current_limit: branchLimit,
      new_limit: newCreditLimit,
      transporter_id: transporterId,
      transporter_name: creditData?.transporter_name,
      remarks: creditRemark,
      branch_name: branch,
      branch_id: branchId,
    };
    const endPoint = `pipe/transporter/v2/branch-wise-credit-limit`;
    try {
      await apiCaller(endPoint, body, `Bearer ${token}`, 'nh-base', 'post');
      handleClose();
      if (getBranchesCreditLimit && activeRole === 'ops') {
        setTimeout(() => {
          getBranchesCreditLimit();
        }, 500);
      }
      if (setActivityRefresh) {
        setTimeout(() => {
          setActivityRefresh();
        }, 500);
      }
      activeRole === 'ops'
        ? swal('Success!', `New Credit Limit set successfully`, 'success')
        : swal(
            'Success!',
            `Request for new Credit Limit sent successfully`,
            'success'
          );
    } catch (error) {
      logger.error(error?.response?.data?.Error);
      swal('Error!', `${error?.response?.data?.Error}`, 'error');
    }
    setCreditLimitUpdating(false);
  }

  useEffect(() => {
    if (creditLimitEditDialog) {
      getCreditLimitDetails();
    }
  }, [creditLimitEditDialog]);

  return (
    <Dialog
      maxWidth="sm"
      open={creditLimitEditDialog}
      TransitionComponent={Transition}
      keepMounted
      onClose={handleClose}
    >
      <DialogTitle disableTypography className={classes.title}>
        <p style={{ fontSize: '20px', fontWeight: 400, margin: 0 }}>
          {branch && activeRole === 'ops'
            ? 'Edit branch’s credit limit'
            : isThresholdExceeded && isCreditLimitExceeded
            ? 'Request to extend limit?'
            : branch && TransporterPercentage && !threshold
            ? 'Request for extension of credit limit on branch Level'
            : threshold && isThresholdExceeded
            ? 'Request to edit trip credit threshold'
            : branch
            ? 'Request to edit credit limit of the branch'
            : 'Edit credit limit'}
        </p>
        <Button
          justIcon
          key="close"
          aria-label="Close"
          color="transparent"
          onClick={handleClose}
        >
          <Close />
        </Button>
      </DialogTitle>
      <DialogContent id="classic-modal-slide-description">
        {creditDataLoading ? (
          <CircularProgress />
        ) : (
          <>
            {TransporterPercentage && isCreditLimitExceeded && (
              <div style={{ marginBottom: '10px' }}>
                <text
                  style={{
                    fontSize: '16px',
                    fontWeight: 400,
                    marginBottom: '10px',
                  }}
                >
                  <span style={{ fontWeight: 700 }}>
                    {creditData?.transporter_name}
                  </span>{' '}
                  has reached <strong>{TransporterPercentage}%</strong> of its
                  branch credit limit. To create a trip, please collect the
                  outstanding balance from the transporter or request an
                  extension of the credit limit.
                </text>
              </div>
            )}
            {threshold && isThresholdExceeded && (
              <div style={{ marginBottom: '10px' }}>
                <text
                  style={{
                    fontSize: '16px',
                    fontWeight: 400,
                    marginBottom: '10px',
                  }}
                >
                  <span style={{ fontWeight: 700 }}>
                    {creditData?.transporter_name}
                  </span>{' '}
                  has reached <strong>{threshold} </strong>of its trip credit
                  threshold. To create a trip, please collect the outstanding
                  advance from the transporter or request an extension.
                </text>
              </div>
            )}
            <div className={classes.cards}>
              <div
                style={{
                  marginBottom: '10px',
                  display: 'flex',
                  justifyContent: branch ? 'space-between' : 'center',
                }}
              >
                <div
                  className={branch ? classes.colFlexStart : classes.flexCol}
                >
                  <p
                    style={{
                      fontSize: '12px',
                      margin: '0px',
                      fontWeight: 500,
                      color: '#474D66',
                    }}
                  >
                    Transporter
                  </p>
                  <text className={classes.transporter}>
                    {creditData?.transporter_name}
                  </text>
                </div>
                {branch && (
                  <div className={classes.colFlexStart}>
                    <p
                      style={{
                        fontSize: '12px',
                        margin: '0px',
                        fontWeight: 500,
                      }}
                    >
                      Branch
                    </p>
                    <text
                      style={{
                        color: '#163BAC',
                        fontSize: '16px',
                        fontWeight: 500,
                      }}
                    >
                      {branch}
                    </text>
                  </div>
                )}
              </div>
              <CreditMetrics data={creditData?.transporter_info} />
            </div>
            <div className={classes.colFlexStart}>
              {activeRole !== 'ops' && isCreditLimitExceeded && (
                <text
                  style={{
                    fontSize: '16px',
                    fontWeight: 400,
                    marginBottom: '10px',
                  }}
                >
                  Do you wish to extend the branch credit limit?
                </text>
              )}
              <div
                style={{
                  justifyContent:
                    activeRole === 'ops' ? 'center' : 'space-between',
                }}
                className={classes.creditLimitSection}
              >
                {activeRole !== 'ops' && isCreditLimitExceeded && (
                  <FormControl component="fieldset">
                    <RadioGroup
                      aria-label="Extend Limit"
                      name="Extend Limit"
                      value={extendLimit}
                      onChange={() => setExtendLimit(!extendLimit)}
                      row
                    >
                      <FormControlLabel
                        value={false}
                        control={<Radio size="small" color="primary" />}
                        label="No"
                      />
                      <FormControlLabel
                        value
                        control={<Radio size="small" color="primary" />}
                        label="Yes"
                      />
                    </RadioGroup>
                  </FormControl>
                )}
                {(activeRole === 'ops' || extendLimit) && (
                  <div className={classes.creditLimit}>
                    <div
                      style={{
                        gap: '10px',
                        minWidth: '115px',
                      }}
                      className={classes.colFlexStart}
                    >
                      <InputLabel className={classes.labelText}>
                        Current credit limit
                      </InputLabel>
                      <text className={classes.weight}>
                        {branchLimit ? `${currencyFormat(branchLimit)}` : '-'}
                      </text>
                    </div>

                    {activeRole === 'ops' && (
                      <Button
                        color="facebook"
                        size="sm"
                        justIcon
                        className={classes.arrow}
                      >
                        <ArrowForwardRounded />
                      </Button>
                    )}
                    <div
                      style={{
                        gap: '5px',
                      }}
                      className={classes.colFlexStart}
                    >
                      <InputLabel className={classes.labelText}>
                        New credit limit requested
                      </InputLabel>
                      <input
                        style={{ padding: '5px' }}
                        min="0"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        id="creditLimit"
                        maxLength="7"
                        value={newCreditLimit}
                        onChange={e => {
                          const value = e.target.value.replace(/\D/g, '');
                          setNewCreditLimit(
                            value === '' ? '' : String(Number(value))
                          );
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
              {threshold && isThresholdExceeded && (
                <text
                  style={{
                    fontSize: '16px',
                    fontWeight: 400,
                    marginBottom: '10px',
                  }}
                >
                  Do you wish to extend the Trip Credit Threshold?
                </text>
              )}
              <div
                style={{
                  justifyContent:
                    activeRole === 'ops' ? 'center' : 'space-between',
                }}
                className={classes.creditLimitSection}
              >
                {threshold && isThresholdExceeded && (
                  <FormControl component="fieldset">
                    <RadioGroup
                      aria-label="Extend Threshold"
                      name="Extend Threshold"
                      value={extendThreshold}
                      onChange={() => setExtendThreshold(!extendThreshold)}
                      row
                    >
                      <FormControlLabel
                        value={false}
                        control={<Radio size="small" color="primary" />}
                        label="No"
                      />
                      <FormControlLabel
                        value
                        control={<Radio size="small" color="primary" />}
                        label="Yes"
                      />
                    </RadioGroup>
                  </FormControl>
                )}
                {extendThreshold && (
                  <div className={classes.creditLimit}>
                    <div
                      style={{
                        gap: '10px',
                        minWidth: '115px',
                      }}
                      className={classes.colFlexStart}
                    >
                      <InputLabel className={classes.labelText}>
                        Current limit
                      </InputLabel>
                      <text className={classes.weight}>
                        {currentThresholdCount}
                      </text>
                    </div>

                    <div
                      style={{
                        gap: '5px',
                      }}
                      className={classes.colFlexStart}
                    >
                      <InputLabel className={classes.labelText}>
                        New limit requested
                      </InputLabel>
                      <input
                        style={{ padding: '5px' }}
                        min="0"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        id="creditLimit"
                        maxLength="7"
                        value={newCreditThreshold}
                        onChange={e => {
                          const value = e.target.value.replace(/\D/g, '');
                          setNewCreditThreshold(
                            value === '' ? '' : Number(value)
                          );
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
              {(activeRole === 'ops' || extendLimit || extendThreshold) && (
                <div
                  style={{
                    width: '100%',
                    marginTop: '15px',
                  }}
                  className={classes.colFlexStart}
                >
                  <text className={classes.weight}>Add Note:</text>
                  <textarea
                    style={{ width: '100%', padding: '10px' }}
                    placeholder="Note"
                    value={creditRemark}
                    onChange={e => setCreditRemark(e.target.value)}
                  />
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
      <DialogActions
        className={classes.modalFooter}
        style={{ padding: '8px 24px' }}
      >
        <Button
          size="md"
          color="white"
          className={classes.cancelButton}
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button
          color="linkedin"
          size="md"
          className={classes.submitButton}
          onClick={() => {
            const isOps = activeRole === 'ops';
            if (extendThreshold && (extendLimit || isOps)) {
              updateCreditLimit();
              updateCreditCount();
            } else if (extendLimit || isOps) {
              updateCreditLimit();
            } else if (extendThreshold) {
              updateCreditCount();
            } else {
              setCreditLimitEditDialog(false);
            }
          }}
          disabled={
            creditLimitUpdating ||
            ((activeRole === 'ops' || extendLimit || extendThreshold) &&
              (!creditRemark ||
                (extendLimit && !newCreditLimit) ||
                (extendThreshold && !newCreditThreshold)))
          }
        >
          {creditLimitUpdating ? (
            <CircularProgress size={15} color="inherit" />
          ) : (
            <>
              {activeRole === 'ops'
                ? 'Submit'
                : extendLimit || extendThreshold
                ? 'Proceed & request for approval'
                : 'Done'}
            </>
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreditLimitEditDialog;
